import React from 'react'
import { IonLabel, IonBadge, IonChip, IonGrid, IonRow, IonCol, IonList } from '@ionic/react'
import './ErrorsAndWarningsList.component.css'
import { getRulesSelector } from '../../rules/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { IDataPoint, IRuleError } from '../../rules/store/rules.types'
import { getPatientTimelineSelector } from '../../timeline/selectors/patientTimeline.selector'
import { setGroupedRulesErrors } from '../../rules/store/rules.actions'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { getPatientInformationState } from '../../patientInformation/selectors'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { getCompleteChartSelector } from '../selectors'
import { CompleteChartConstants } from '../store/completeChart.constants'
import { isTablet } from '../../../utilities'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { setCategories, setReportType, setRuleId } from '../../specialReports/store/specialReports.actions'
import { getSpecialReportOptionsSelector } from '../../options/selectors/specialReportOptions.selector'

const ErrorsAndWarningsList: React.FC = () => {
  const rulesState = useSelector(getRulesSelector)
  const { groupedRulesErrors } = rulesState
  const { categories } = useSelector(getSpecialReportOptionsSelector)
  const dispatch = useDispatch()
  const history = useHistory()
  const timelineState = useSelector(getPatientTimelineSelector)
  const { selectedOption } = useSelector(getCompleteChartSelector)
  const { items } = timelineState
  const serviceConfig = useSelector(getServiceConfigSelector)
  const { FORCE_PT_SEARCH } = serviceConfig
  const patientInformation = useSelector(getPatientInformationState)
  const { searchImport, newPatient } = patientInformation
  const isTabletDevice = isTablet()

  const handleSpecialReports = (error: IRuleError) => {
    dispatch(setRuleId(error.id))
    dispatch(setReportType('rule'))
    const category = categories.find((c) => c.elementId === error.specialReportCategoryId?.toString())
    const categoryOption = { ...category, id: Number(category?.elementId), name: category?.label }
    dispatch(setCategories(category ? [categoryOption] : []))
    history.push(AppRouterConstants.SPECIAL_REPORTS_ROUTE)
  }

  const rulesErrorOnClick = (error: IRuleError, navRoute: string) => {
    if (error.errorMessage.startsWith('SPECIAL REPORT')) {
      handleSpecialReports(error)
      return
    }
    if (error.cardinalityKey?.length) {
      const item = items.find((i) => i.key === error.cardinalityKey)
      if (item) {
        dispatch({
          type: item.rowActionType,
          payload: item,
        })
      }
    }
    if (!navRoute || navRoute == null || navRoute === '' || navRoute === undefined) {
      return
    }
    dispatch(setGroupedRulesErrors({}))

    if (
      FORCE_PT_SEARCH === 1 &&
      !searchImport &&
      !newPatient &&
      (navRoute === TabConstants.PATIENT_DEMOGRAPHICS ||
        navRoute === TabConstants.BARRIERS_TO_CARE ||
        navRoute === TabConstants.DO_NOT_RESUSCITATE ||
        navRoute === TabConstants.PATIENT_MEDICAL_HISTORY)
    ) {
      history.push(TabConstants.PATIENT_INFO_ROUTE)
    } else if (navRoute === TabConstants.BARRIERS_TO_CARE || navRoute === TabConstants.DO_NOT_RESUSCITATE) {
      history.push(isTabletDevice ? TabConstants.PATIENT_INFO_ROUTE : TabConstants.PATIENT_DEMOGRAPHICS)
    } else {
      history.push(navRoute)
    }
  }

  const getErrorTypeLabel = (errType: string) => {
    const errorTypeUpper = errType.toUpperCase()
    switch (errorTypeUpper) {
      case 'STANDARD':
        return 'STANDARD'
      case 'XSD':
      case 'SCHEMATRON':
        return 'NEMSIS'
      case 'EMSCHARTS':
        return 'SERVICE'
      default:
        return errorTypeUpper
    }
  }

  return (
    <IonGrid className='ion-no-padding mt-1 mb-10rem'>
      <IonList>
        {selectedOption === CompleteChartConstants.SET_ERRORS_TAB &&
          Object.values(groupedRulesErrors)
            .flat()
            .filter((error) => error.errorType === 'error')
            .map((error, index, arr) => (
              <div key={`${error.id}${index + 1}`}>
                <IonRow className='ion-align-items-start'>
                  <IonCol size='9'>
                    <IonChip className='ml-8 sc-danger-subtle'>
                      <IonBadge data-testid={`ValidationCountBadge${index + 1}`} color='danger'>
                        {index + 1}
                      </IonBadge>
                      {error.dataPoints
                        .filter((datapoint: IDataPoint) => datapoint.label != null)
                        .map((dataPoint: IDataPoint, count) => (
                          <React.Fragment key={dataPoint.dataModelPath}>
                            <IonLabel
                              tabIndex={0}
                              className='error-link-chip'
                              key={dataPoint.dataModelPath}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') rulesErrorOnClick(error, dataPoint.navRoute || '')
                              }}
                              onClick={() => {
                                rulesErrorOnClick(error, dataPoint.navRoute || '')
                              }}>
                              <h3>
                                {dataPoint.parentLabel === 'Document in Web'
                                  ? dataPoint.label + ' (' + dataPoint.parentLabel + ') '
                                  : dataPoint.label}{' '}
                                {error.priority === 0 ? '*' : ''}
                              </h3>
                            </IonLabel>
                            <span className='divider'>
                              {' '}
                              &nbsp;
                              {count <
                                error.dataPoints.filter((datapoint: IDataPoint) => datapoint.label != null).length -
                                  1 && ' | '}
                            </span>
                          </React.Fragment>
                        ))}
                    </IonChip>

                    <h6 className='error-message-text'>{error.errorMessage}</h6>
                  </IonCol>
                  <IonCol size='3' className='error-message-type-text'>
                    {error.priority === 0 ? 'REQUIRED' : getErrorTypeLabel(error.sourceType)}
                  </IonCol>
                </IonRow>
                {index !== arr.length - 1 && <hr />}
              </div>
            ))}

        {selectedOption === CompleteChartConstants.SET_WARNINGS_TAB &&
          Object.values(groupedRulesErrors)
            .flat()
            .filter((error) => error.errorType === 'warning')
            .map((error, index, arr) => {
              return (
                <div key={`${error.id}${index + 1}`}>
                  <IonRow className='ion-align-items-start'>
                    <IonCol size='9'>
                      <IonChip className='ml-8 sc-warning-subtle'>
                        <IonBadge data-testid={`ValidationCountBadge${index + 1}`} color='warning'>
                          {index + 1}
                        </IonBadge>
                        {error.dataPoints
                          .filter((datapoint: IDataPoint) => datapoint.label != null)
                          .map((dataPoint: IDataPoint, count) => (
                            <React.Fragment key={dataPoint.dataModelPath}>
                              <IonLabel
                                tabIndex={0}
                                className='error-link-chip'
                                key={dataPoint.dataModelPath}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') rulesErrorOnClick(error, dataPoint.navRoute || '')
                                }}
                                onClick={() => {
                                  rulesErrorOnClick(error, dataPoint.navRoute || '')
                                }}>
                                <h3>
                                  {dataPoint.parentLabel === 'Document in Web'
                                    ? dataPoint.label + '(' + dataPoint.parentLabel + ')'
                                    : dataPoint.label}
                                </h3>
                              </IonLabel>
                              <span className='divider'>
                                {' '}
                                &nbsp;
                                {count <
                                  error.dataPoints.filter((datapoint: IDataPoint) => datapoint.label != null).length -
                                    1 && ' | '}
                              </span>
                            </React.Fragment>
                          ))}
                      </IonChip>

                      <h6 className='error-message-text'>{error.errorMessage}</h6>
                    </IonCol>
                    <IonCol size='3' className='error-message-type-text'>
                      {getErrorTypeLabel(error.sourceType)}
                    </IonCol>
                  </IonRow>
                  {index !== arr.length - 1 && <hr />}
                </div>
              )
            })}
      </IonList>
    </IonGrid>
  )
}

export default ErrorsAndWarningsList
